import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd, RouterModule } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { HttpSharedServiceService } from './utility/services/shared-http/http-shared-service.service';
import { StaticLogService } from './utility/services/static-log/static-log.service';
import { UserService } from './utility/services/user/user.service';
import { USER_TOKEN } from './utility/authentication/user-token.util';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { MaterialModule } from "./common/material-module";
import { SearchComponent } from './common/search/search.component';
import { SpinnerComponent } from './common/spinner/spinner.component';
import { NotificationHistoryComponent } from "./common/notification-history/notification-history.component";
import { RoleEnum } from './common/interfaces/token/role-enum';
import { MatDialog } from '@angular/material/dialog';
import { RoleImpersonationDialogComponent } from './common/dialogs/role-impersonation-dialog/role-impersonation-dialog.component';
import { ContactService } from './utility/services/contact/contact.service';
import { PatchNotesService } from './utility/services/patch-notes/patch-notes.service';
import * as pkg from '../../package.json';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    imports: [MaterialModule, RouterOutlet, SearchComponent, RouterModule, SpinnerComponent, NotificationHistoryComponent, AsyncPipe]
})
export class AppComponent implements OnInit, OnDestroy {


  private readonly _destroying$ = new Subject<void>();

  router = inject(Router);
  private _http = inject(HttpSharedServiceService);
  log = inject(StaticLogService)
  roles = RoleEnum;
  dialog = inject(MatDialog);

  claims: any[] = [];
  title = 'Quickparts';
  isSidebarActive: boolean = true;
  isHeaderActive: boolean = true;
  token = inject(USER_TOKEN);
  #authService = inject(MsalService);
  #msalBroadcastService = inject(MsalBroadcastService);
  userService = inject(UserService);
  contactService = inject(ContactService);
  patchNotesService = inject(PatchNotesService);
  pagename?: string | null;
  role?: string | null;
  namecanon?: string | null;
  quote_details: any;
  user: any;
  darkMode = false;
  requestedpage: string | null = localStorage.getItem('requestedpage');
  appVersion = (pkg as any).version || '';
  isImpersonating = false;


  constructor() {
    const router = this.router;

    if (this.router.url === '/') {
      console.log("Router url was blank");
    } else {
      this.getname(this.router.url);
    }
    router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.getname(event.url);
      }
    })
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  ngOnInit() {
    this.#authService.handleRedirectObservable().subscribe();

    this.#msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe((result: EventMessage) => {
        if (this.#authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
        }
      });

    //To subscribe for claims
    this.userService.claims$.subscribe((c) => {
      this.claims = c;
    });

    this.#msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.userService.checkAndSetActiveAccount();
        this.userService.getUserToken();
      });

    // Subscribe to token changes to update impersonation status
    this.token.subscribe(token => {
      this.isImpersonating = token?.IsFake == "true"
    });

    const darkModePref = localStorage.getItem('darkMode');
    this.darkMode = darkModePref === 'true';
    this.toggleDarkTheme(this.darkMode);

    let windowurl = window.location.href;
    if (windowurl.includes('Contact')) {
      let contactid = windowurl.substring(windowurl.lastIndexOf('/') + 1)
      if (
        contactid &&
        contactid !== 'null' &&
        contactid !== 'undefined' &&
        contactid !== null &&
        contactid !== undefined
      ) {
        this.router.navigate([`/Contact/${contactid}`]);
        localStorage.setItem('requestedpage', 'Contact');
        localStorage.setItem('pageid', contactid);
      } else {
        this.router.navigate(['/Recent/']);
      }
    }

  }

  sidebarToggle() {
    this.isSidebarActive = !this.isSidebarActive;
  }
  getname(daname: string) {
    console.log(daname);
    let id = ''
    this.quote_details = localStorage.getItem('selected_quote')
    if (this.quote_details) {
      id = JSON.parse(this.quote_details).OpportunityID
    }
    switch (daname) {
      case '/sales': this.pagename = "Sales Management"; break;

      // Sales Quuote Section
      case '/sales/quotes': this.pagename = "Quotes"; break;
      case '/sales/quotes/new-quote': this.pagename = "Quote Management"; break;
      case `/sales/quote/*`: this.pagename = "Quote Details"; break;
      // One Page ECA Quote
      case this.match(daname, '/Contact*'): this.pagename = "Contact"; break;

      // Order — matched before the generic quote pattern below, which would
      // otherwise swallow it and title the page "Quote Details".
      case this.match(daname, '/sales/quote/order/*'): this.pagename = "Order"; break;

      // One Page ECA Quote
      case this.match(daname, '/sales/quote/*'): this.pagename = "Quote Details"; break;

      // Production (formerly Supplier / Project)
      case this.match(daname, '/Production*'): this.pagename = "Production"; break;
      case this.match(daname, '/Supplier*'): this.pagename = "Production"; break;
      case this.match(daname, '/Project*'): this.pagename = "Production"; break;

      // Recently Viewed
      case this.match(daname, '/Recent'): this.pagename = "Recently Viewed"; break;

      // What's New (patch notes)
      case this.match(daname, '/Whats-New'): this.pagename = "What's New"; break;

      default: this.pagename = ""; break;
    }
  }

  match(value: string, pattern: string): string {
    const regexPattern = new RegExp(`^${pattern.replace(/\*/g, '.*')}$`);
    return regexPattern.test(value) ? value : "no";
  }

  isNumber(value: any): boolean {
    return value !== null && value !== undefined && !isNaN(Number(value));
  }
  toggleDarkTheme(isDark: boolean) {
    const body = document.body;
    if (isDark) {
      body.classList.add('dark-theme');
      localStorage.setItem('darkMode', 'true');
    } else {
      body.classList.remove('dark-theme');
      localStorage.setItem('darkMode', 'false');
    }
  }

  logout() {
    this.userService.logout();
  }

  openImpersonationDialog() {
    const dialogRef = this.dialog.open(RoleImpersonationDialogComponent, {
      width: '400px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.length > 0) {
        this.userService.impersonateRoles(result);
      }
    });
  }

  endImpersonation() {
    this.userService.endImpersonation();
  }

}
